import { useState } from "react";
import PaginationComponent from "@/app/components/ui/Pagination";

export const DEFAULT_PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

const CONTROL_CLASS =
  "border border-gray-300 rounded px-2 py-1 text-[0.75rem] font-semibold text-black disabled:opacity-40 disabled:cursor-not-allowed";
const LABEL_CLASS = "text-[#727272] text-[0.75rem] font-semibold";

export interface IPaginationBarProps {
  /** The page the SERVER returned, not what was last clicked. */
  currentPage: number;
  /** Rows per page the server applied. */
  pageSize: number;
  /** Size of the whole filtered set, not of the current page. */
  totalCount: number;

  onPageChange: (page: number, pageSize: number) => void;
  pageSizeOptions?: number[];
  /** Shown in place of the range when there is nothing to page through. */
  emptyLabel?: string;
  /** Extra classes on the outer grid. */
  className?: string;
}

const PaginationBar: React.FC<IPaginationBarProps> = ({
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  emptyLabel = "No records",
  className = "",
}) => {
  const [jumpToPage, setJumpToPage] = useState<string>("");

  const pageCountFor = (size: number) => Math.max(Math.ceil(totalCount / size), 1);
  const totalPages = pageCountFor(pageSize);

  const firstRowOnPage = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastRowOnPage = Math.min(currentPage * pageSize, totalCount);

  const goTo = (page: number, size: number = pageSize) => {
    const target = Math.min(Math.max(Math.trunc(page) || 1, 1), pageCountFor(size));
    onPageChange(target, size);
  };

  return (
    <div className={`grid grid-cols-1 items-center gap-3 p-4 w-full sm:grid-cols-[1fr_auto_1fr] ${className}`}>
      <span className={`justify-self-center ${LABEL_CLASS} sm:justify-self-start`}>
        {totalCount === 0 ? emptyLabel : `Showing ${firstRowOnPage}-${lastRowOnPage} of ${totalCount}`}
      </span>

      <div className="flex flex-wrap items-center justify-center gap-2">
        <button type="button" className={CONTROL_CLASS} disabled={currentPage <= 1} onClick={() => goTo(1)}>
          &laquo; First
        </button>

        <PaginationComponent
          className="pagination-bar"
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={(page) => goTo(page)}
        />

        <button
          type="button"
          className={CONTROL_CLASS}
          disabled={currentPage >= totalPages}
          onClick={() => goTo(totalPages)}
        >
          Last &raquo;
        </button>

        <form
          className="flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            const requested = Number(jumpToPage);
            if (requested) goTo(requested);
            setJumpToPage("");
          }}
        >
          <label className={LABEL_CLASS} htmlFor="pagination-jump-to-page">
            Go to
          </label>
          <input
            id="pagination-jump-to-page"
            type="number"
            min={1}
            max={totalPages}
            value={jumpToPage}
            placeholder={`1-${totalPages}`}
            onChange={(e) => setJumpToPage(e.target.value)}
            className={`w-[76px] ${CONTROL_CLASS}`}
          />
        </form>
      </div>

      <label className={`flex items-center justify-self-center gap-2 ${LABEL_CLASS} sm:justify-self-end`}>
        Rows
        <select
          className={CONTROL_CLASS}
          value={pageSize}
          onChange={(e) => goTo(1, Number(e.target.value))}
        >
          {pageSizeOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
};

export default PaginationBar;
